import { memo } from "react";
import {
  Button,
  Chip,
  CircularProgress,
  Paper,
  Stack,
  Switch,
  Tooltip,
  Typography,
} from "@mui/material";
import { Business } from "@mui/icons-material";
import dayjs from "dayjs";
import { useFormatter } from "@/utils/useFormatter";
import type { TeleconsultoriaAffiliationRow } from "../types";

interface AffiliationCardProps {
  row: TeleconsultoriaAffiliationRow;
  isPending: boolean;
  /** Bloqueia o switch enquanto outra linha está salvando. */
  isDisabled: boolean;
  onToggleActive: (row: TeleconsultoriaAffiliationRow, isActive: boolean) => void;
  /** Pedido da afiliada: Aprovar (true) / Recusar (false) no lugar do switch. */
  onAnswerRequest?: (row: TeleconsultoriaAffiliationRow, isApproved: boolean) => void;
}

/** Aprovar/Recusar de um pedido (mobile: abaixo do nome, largura total). */
function RequestActions({
  row,
  isDisabled,
  onAnswerRequest,
}: Pick<AffiliationCardProps, "row" | "isDisabled" | "onAnswerRequest">) {
  return (
    <Stack direction="row" spacing={1} sx={{ width: { xs: "100%", sm: "auto" } }}>
      <Button
        size="small"
        variant="contained"
        disabled={isDisabled}
        onClick={() => onAnswerRequest?.(row, true)}
        sx={{ textTransform: "none", flex: { xs: 1, sm: "none" } }}
      >
        Aprovar
      </Button>
      <Button
        size="small"
        color="error"
        variant="outlined"
        disabled={isDisabled}
        onClick={() => onAnswerRequest?.(row, false)}
        sx={{ textTransform: "none", flex: { xs: 1, sm: "none" } }}
      >
        Recusar
      </Button>
    </Stack>
  );
}

/** Uma organização atendida: nome, CNPJ, desde quando e o switch de ativação. */
export const AffiliationCard = memo(function AffiliationCard({
  row,
  isPending,
  isDisabled,
  onToggleActive,
  onAnswerRequest,
}: AffiliationCardProps) {
  const sinceLabel = row.since ? dayjs(row.since).format("DD/MM/YYYY") : undefined;
  const toggleLabel = row.isActive ? "Desativar afiliação" : "Reativar afiliação";
  const isRequest = row.isPendingRequest && Boolean(onAnswerRequest);

  return (
    <Paper
      variant="outlined"
      role="listitem"
      sx={{
        p: 1.5,
        borderLeft: "4px solid",
        borderLeftColor: isRequest ? "warning.main" : row.isActive ? "success.main" : "divider",
        borderRadius: "0 8px 8px 0",
        // Inativas ficam esmaecidas, mas continuam acessíveis para reativar.
        opacity: row.isActive || isRequest ? 1 : 0.7,
      }}
    >
      <Stack direction="row" spacing={1.5} alignItems="center" flexWrap={isRequest ? "wrap" : "nowrap"} useFlexGap>
        <Business color={row.isActive || isRequest ? "primary" : "disabled"} aria-hidden />
        <Stack spacing={0.25} sx={{ flex: 1, minWidth: 0 }}>
          <Typography variant="subtitle1" fontWeight={700} noWrap>
            {row.receiverName}
          </Typography>
          <Typography variant="body2" color="text.secondary" noWrap>
            CNPJ {useFormatter.formatCNPJ(row.receiverCnpj)}
            {sinceLabel ? ` · ${isRequest ? "pedido em" : "desde"} ${sinceLabel}` : ""}
          </Typography>
        </Stack>
        <Chip
          size="small"
          label={isRequest ? "Pedido pendente" : row.isActive ? "Ativa" : "Inativa"}
          color={isRequest ? "warning" : row.isActive ? "success" : "default"}
          variant={row.isActive || isRequest ? "filled" : "outlined"}
          sx={{ display: { xs: "none", sm: "inline-flex" } }}
        />
        {isPending ? (
          <CircularProgress size={24} aria-label="Salvando" />
        ) : isRequest ? (
          <RequestActions row={row} isDisabled={isDisabled} onAnswerRequest={onAnswerRequest} />
        ) : (
          <Tooltip title={toggleLabel}>
            <span>
              <Switch
                checked={row.isActive}
                disabled={isDisabled}
                onChange={(_, checked) => onToggleActive(row, checked)}
                inputProps={{ "aria-label": `${toggleLabel}: ${row.receiverName}` }}
              />
            </span>
          </Tooltip>
        )}
      </Stack>
    </Paper>
  );
});
